import React from 'react';

/**
 * Public Board Report page.
 * Route: /board-report
 * Shareable link — no auth required.
 */
export default function BoardReport() {
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 print:bg-white">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          body { font-size: 10pt; }
        }
        table { border-collapse: collapse; }
      `}</style>

      {/* Header / Cover */}
      <header className="bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
          <p className="text-xs uppercase tracking-[0.2em] opacity-80 mb-3">Board Report</p>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4" data-testid="board-report-title">
            Taptile
          </h1>
          <p className="text-lg sm:text-xl opacity-90 max-w-2xl">
            Multi-tenant PWA + native iOS platform for schools and member-based organisations.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm opacity-90">
            <div><span className="opacity-60">Date:</span> <strong>{dateStr}</strong></div>
            <div><span className="opacity-60">Prepared by:</span> <strong>Phil Reardon</strong></div>
            <div><span className="opacity-60">Status:</span> <strong>Active development, pre-GA</strong></div>
          </div>
          <div className="mt-8 flex gap-3 no-print">
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="bg-white text-orange-700 hover:bg-orange-50 px-5 py-2 rounded-full text-sm font-semibold shadow-md transition-colors"
            >
              Print / Save as PDF
            </button>
            <button
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard?.writeText(url);
                alert('Link copied — paste it anywhere to share.');
              }}
              data-testid="copy-link-btn"
              className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-5 py-2 rounded-full text-sm font-semibold transition-colors"
            >
              Copy share link
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 sm:py-14 space-y-12">

        {/* 1. Executive Summary */}
        <Section num="1" title="Executive Summary">
          <p>
            Taptile is a multi-tenant PWA + native iOS platform that lets organisations (primarily schools) give their
            community a branded portal with tiles, news, push notifications, and contact sync. We have a functioning
            end-to-end product with one school (<strong>St. Andrews 107</strong>) piloting a native iOS app.
            Core infrastructure is now production-grade, and we are entering the commercialisation phase.
          </p>
        </Section>

        {/* 2. What's Been Built */}
        <Section num="2" title="What's Been Built" emoji="✅">
          <SubHeading>2.1 Core SaaS Platform (Web)</SubHeading>
          <Bullets items={[
            'Multi-tenant FastAPI backend + MongoDB, with full tenant isolation',
            'Responsive React admin dashboard (Design, Content, Groups, Notify, Connect)',
            'Public web portal per tenant (/portal/{customer_id}) with branded tiles',
            'Customisable PWA — colours, fonts, icons, banner, tile styles, grid layout',
            'Authentication: email/password + Google SSO',
            'Super Admin console for managing tenants and platform-wide settings',
            'Subscription-ready plan infrastructure (Stripe pending)',
            'Full audit log of admin actions',
          ]} />

          <SubHeading>2.2 Content & Communication</SubHeading>
          <Bullets items={[
            'Article management — image uploads, display ordering, preview, image library',
            'Push notifications — Web Push (VAPID) + iOS (Expo/APNs)',
            'Notification scheduling, group targeting, full history',
            'Tenant-configurable article count (1, 2, or 3 visible)',
            'Social links section with per-tenant styling',
          ]} />

          <SubHeading>2.3 Native iOS App (Expo / EAS Build)</SubHeading>
          <Bullets items={[
            'Single codebase serves every tenant — pulls branding dynamically from server',
            'Login with short code (e.g. STANDREWS) instead of long customer ID',
            'Authentication + session persistence (AsyncStorage)',
            'Home screen with tiles + latest articles',
            'Inbox with unread count, mark-as-read, deletion',
            'Push notifications (Expo Push Service)',
            'Pull-to-refresh',
            'Version control check — force-upgrade older clients',
            'Custom app icon + splash screen',
          ]} />

          <SubHeading>2.4 Infrastructure & Reliability</SubHeading>
          <Bullets items={[
            'Persistent image storage on DigitalOcean Spaces (S3) — deploys no longer wipe tenant banners/icons',
            'Kubernetes-hosted dev and preview environments',
            'MongoDB Atlas for production data',
            'Emergent-managed deploy pipeline to taptile.app',
          ]} />

          <SubHeading>2.5 Integrations</SubHeading>
          <Bullets items={[
            'NexusCMS API (live) — REST + bulk upsert, X-API-Key auth, per-tenant key. Admin UI shipped.',
            'Wonde (scaffolded) — API token storage, manual sync endpoint. Auto-sync pending.',
            'SendGrid — transactional email (welcome, password reset, notifications)',
          ]} />
        </Section>

        {/* 3. Live Metrics */}
        <Section num="3" title="Live Metrics / Pilot Status" emoji="📊">
          <Table
            headers={['Item', 'Status']}
            rows={[
              ['Tenants configured', '~5 (mostly staging/test)'],
              ['Active live tenant', 'St. Andrews 107 (iOS pilot)'],
              ['iOS app installs (pilot)', '1 device (founder testing)'],
              ['Contacts API', 'Keyed, tested, ready for NexusCMS hand-off'],
              ['Push notifications', 'Working on iOS + web'],
            ]}
          />
        </Section>

        {/* 4. Roadmap */}
        <Section num="4" title="Roadmap" emoji="🗺️">
          <RoadmapBlock
            title="🔴 Short-term (next 2–4 weeks) — Pre-go-live essentials"
            rows={[
              ['1', 'Deploy latest web changes to taptile.app (Connect tab, S3 uploads)', 'Phil', 'This week'],
              ['2', 'Issue real API key to NexusCMS team + complete first contact sync', 'Phil + NexusCMS', 'W/c 5 May'],
              ['3', 'Rebuild iOS app (banner + multi-article) on EAS quota reset', 'Phil', '2 May'],
              ['4', 'St. Andrews 107 pilot — hand app to 2–3 real staff for live feedback', 'Phil + St.A', 'W/c 5 May'],
              ['5', 'Tie NexusCMS contacts → iOS app user auth (login with real email)', 'Dev', 'Mid May'],
              ['6', 'Full QA pass on multi-tenant flow (signup → design → deploy → iOS)', 'Dev', 'Mid May'],
            ]}
          />

          <RoadmapBlock
            title="🟠 Medium-term (1–3 months) — Commercial launch"
            rows={[
              ['7', 'Stripe Billing integration (subscription plans)', 'Revenue unlock', 'End May'],
              ['8', 'Self-serve public signup → plan → pay → onboarded in minutes', 'Scale', 'Early June'],
              ['9', 'Wonde daily auto-sync + sync status dashboard', 'Core for schools', 'June'],
              ['10', 'App Store submission (privacy, support URL, screenshots, review)', 'Distribution', 'End June'],
              ['11', 'QR code generator (admin) + scanner (iOS) for one-tap login', 'UX polish', 'June'],
              ['12', 'Microsoft SSO (schools heavily use M365)', 'Enterprise deals', 'June/July'],
              ['13', 'Super Admin dashboard — plan usage, revenue, tenant health', 'Ops visibility', 'July'],
            ]}
          />

          <RoadmapBlock
            title="🟡 Long-term (3–6 months) — Scale"
            rows={[
              ['14', 'Android app (Expo supports — mostly config + store work)', '2× addressable market', 'Aug/Sep'],
              ['15', 'Analytics dashboard per tenant (tile clicks, engagement, open rates)', 'Retention', 'August'],
              ['16', 'White-label / reseller tier (agencies running apps for schools)', 'New revenue channel', 'September'],
              ['17', 'Multi-school support for Multi-Academy Trusts (MATs)', 'Market expansion', 'Sep/Oct'],
              ['18', 'Field-mapping UI for NexusCMS/Wonde (tenant chooses what syncs)', 'Flexibility', 'October'],
              ['19', 'In-app forms (permission slips, absence reports) with storage', 'Stickiness', 'Q4'],
              ['20', 'Backend refactor (5,000-line monolith → modular routers) + iOS refactor', 'Tech debt', 'Ongoing'],
            ]}
          />

          <RoadmapBlock
            title="🔵 Strategic bets (6–12 months) — Optional, discuss at board"
            rows={[
              ['A', 'Parent messaging (2-way chat between parent and school)', 'Biggest single unlock for schools', ''],
              ['B', 'Events + RSVP inside the app', 'Differentiator vs competitors', ''],
              ['C', 'In-app payments (Stripe Connect) for trips, lunches', 'New revenue stream + 1–2% cut', ''],
              ['D', 'Generative AI assistant for admins (draft newsletters, translate)', 'Efficiency sell', ''],
              ['E', 'Expand to non-school verticals (clubs, gyms, churches, councils)', '10× TAM, low eng. cost', ''],
            ]}
            columns={['Item', 'Rationale', '']}
          />
        </Section>

        {/* 5. Risks */}
        <Section num="5" title="Risks & Mitigations" emoji="⚠️">
          <Table
            headers={['Risk', 'Likelihood', 'Impact', 'Mitigation']}
            rows={[
              ['Expo free-plan build limits blocking iterative iOS dev', 'High', 'Medium', 'Upgrade to Expo Production plan ($19/mo) when active iOS dev resumes'],
              ['Apple App Store rejection delays launch', 'Medium', 'High', 'Start submission July with 2–3 week buffer; follow guidelines strictly'],
              ['Single founder (Phil) = bus factor', 'High', 'High', 'Document admin + deploy procedures; onboard a dev contractor by Q3'],
              ['NexusCMS integration gaps', 'Medium', 'Medium', 'Strong spec already shared; test sync with real school data in May'],
              ['MongoDB bloat from historical base64 images', 'Low', 'Low', 'One-off cleanup script planned (backlog)'],
            ]}
          />
        </Section>

        {/* 6. Decisions */}
        <Section num="6" title="Key Decisions Needed from Board" emoji="🤝">
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Pricing tier</strong> — what do we charge per school? (Suggest £15–30/month basic, £50+ with Wonde/NexusCMS)</li>
            <li><strong>Sales motion</strong> — direct (Phil), partner (NexusCMS resells), or self-serve only?</li>
            <li><strong>Android priority</strong> — launch iOS first (July/Aug) then Android, or parallel?</li>
            <li><strong>Funding</strong> — do we want/need capital before pushing into non-school verticals?</li>
            <li><strong>First hire</strong> — when (Q3?) and for what role (engineering / sales / customer success)?</li>
          </ol>
        </Section>

        {/* 7. Asks */}
        <Section num="7" title="Ask of the Board" emoji="🙏">
          <Bullets items={[
            'Approval to proceed with St. Andrews 107 pilot extension (2–3 staff test week of 5 May)',
            'Sign-off on the proposed pricing (£15–30 base, £50 premium) before Stripe integration',
            'Decision on partner strategy with NexusCMS',
            'Budget for Expo Production subscription + Apple Developer Program renewal (~£300/year total)',
          ]} />
        </Section>

        {/* Footer */}
        <footer className="pt-8 border-t border-slate-200 text-sm text-slate-500">
          <p><strong>Next board review:</strong> end of May 2026 — targeting the first paying customer by then.</p>
          <p className="mt-2">© {today.getFullYear()} Taptile. Confidential — for board review only.</p>
        </footer>
      </main>
    </div>
  );
}

/* ---------- small helpers ---------- */

function Section({ num, title, emoji, children }) {
  return (
    <section>
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 border-b-4 border-orange-500 pb-2 mb-5 flex items-baseline gap-3">
        <span className="text-orange-600 font-black">{num}.</span>
        <span>{title}</span>
        {emoji && <span className="text-2xl">{emoji}</span>}
      </h2>
      <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function SubHeading({ children }) {
  return (
    <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">{children}</h3>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead className="bg-slate-100 text-slate-700">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left font-semibold px-4 py-2 border-b border-slate-200">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2 border-b border-slate-100 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoadmapBlock({ title, rows, columns }) {
  const headers = columns || ['#', 'Item', 'Owner / Value', 'Est. completion'];
  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-3 text-slate-800">{title}</h3>
      <Table headers={headers} rows={rows} />
    </div>
  );
}
